import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

interface IOtpInputProps {
    value: string;
    onChange: (value: string) => void;
    onComplete?: (value: string) => void;
    length?: number;
    disabled?: boolean;
}

function buildNextValue(currentValue: string, nextDigit: string, index: number, length: number) {
    const nextValue = currentValue.padEnd(length, " ").split("");
    nextValue[index] = nextDigit;
    return nextValue.join("").replace(/\s+$/g, "");
}

export default function OtpInput({
    value,
    onChange,
    onComplete,
    length = 6,
    disabled = false,
}: IOtpInputProps) {
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    const focusInput = (index: number) => {
        inputRefs.current[index]?.focus();
        inputRefs.current[index]?.select();
    };

    const handleChange = (index: number, inputValue: string) => {
        const nextDigit = inputValue.replace(/\D/g, "").slice(-1);
        const nextValue = buildNextValue(value, nextDigit, index, length);

        onChange(nextValue);

        if (nextDigit && index < length - 1) {
            focusInput(index + 1);
        }

        if (nextValue.length === length && !nextValue.includes(" ")) {
            onComplete?.(nextValue);
        }
    };

    const handleKeyDown = (index: number, key: string) => {
        if (key !== "Backspace") {
            return;
        }

        if (value[index]) {
            const nextValue = buildNextValue(value, "", index, length);
            onChange(nextValue);
            return;
        }

        if (index > 0) {
            focusInput(index - 1);
        }
    };

    const handlePaste = (pastedValue: string) => {
        const digits = pastedValue.replace(/\D/g, "").slice(0, length);

        if (!digits) {
            return;
        }

        onChange(digits);

        if (digits.length === length) {
            onComplete?.(digits);
            focusInput(length - 1);
            return;
        }

        focusInput(digits.length);
    };

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1,
                justifyContent: "center",
                flexWrap: "wrap",
            }}
        >
            {Array.from({ length }).map((_, index) => (
                <TextField
                    key={index}
                    value={value[index] || ""}
                    onChange={(event) => {
                        handleChange(index, event.target.value);
                    }}
                    onKeyDown={(event) => {
                        handleKeyDown(index, event.key);
                    }}
                    onPaste={(event) => {
                        event.preventDefault();
                        handlePaste(event.clipboardData.getData("text"));
                    }}
                    disabled={disabled}
                    inputRef={(element) => {
                        inputRefs.current[index] = element;
                    }}
                    slotProps={{
                        htmlInput: {
                            inputMode: "numeric",
                            maxLength: 1,
                            style: {
                                textAlign: "center",
                            },
                        },
                    }}
                    sx={{ width: 56 }}
                />
            ))}
        </Box>
    );
}
